import type { DetailedBreakdown, IDealerFee, OfferType, DetailedBreakdownItem, GenericObject } from '@makemydeal/dr-platform-types';
import { TaxLabels } from '@makemydeal/dr-platform-types';
import { TaxLabelMappings, LEASE } from '@makemydeal/dr-platform-types';
import { roundToTwoDecimal } from '@makemydeal/dr-common-utils';
import type { TaxItemCategoryItem } from '@makemydeal/dr-platform-types/typedefs/shared/types/paymentServices';
import * as tradeInSelectors from '../selectors/tradeInSelectors';
import * as multipleTradeInsSelectors from '../selectors/multipleTradeInsSelectors';
import * as incentiveSelectors from '../selectors/incentiveSelectors';
import * as accessoriesSelectors from '../selectors/accessoriesSelectors';
import * as taxSelectors from '../selectors/taxSelectors';
import * as offerNodeSelectors from '../selectors/offerNodeSelectors';
import * as shopperSelectors from '../selectors/shopperSelectors';
import * as feesSelectors from '../selectors/feesSelectors';
import * as selectedTermSelectors from '../selectors/selectedTermSelectors';
import * as vehicleProtectionSelectors from '../selectors/vehicleProtectionSelectors';
import type { NetTradeInEquity } from '@makemydeal/dr-platform-types/typedefs/shared/types/pdfDeal';
import { isNotDealerCash } from './incentives';

export type BreakdownItem = {
    name: string;
    value: number;
};
export type GetFeesFn = (state: GenericObject, offerType: OfferType) => DetailedBreakdownItem[];
export type GetTaxesFn = (state: GenericObject, offerType: OfferType) => DetailedBreakdown;
export type PrepareTaxesAndFeesBreakdownDeps = {
    getFeesFn: GetFeesFn;
    getTaxesFn: GetTaxesFn;
};

// Accessories
export const getAccessoriesBreakdown = (state: any): DetailedBreakdown => {
    return {
        value: accessoriesSelectors.getAccessoriesTotalAmount(state),
        items: accessoriesSelectors.getAccessoriesList(state).map((item: any) => {
            return {
                name: item.name,
                value: item.amount
            };
        })
    };
};

// Incentives
/**
 * Conditional incentives for the PDF breakdown.
 *
 * Uses the display selectors so an overridden program incentive is listed once rather than twice —
 * unlabelled the second time — and so the printed total does not count it twice (DE761820).
 *
 * @param state
 * @param offerType
 * @returns
 */
export const getConditionalIncentivesBreakdown = (state: GenericObject, offerType: OfferType): DetailedBreakdown => {
    return {
        value: incentiveSelectors.getAppliedConditionalIncentivesAmountForDisplay(state, offerType),
        items: incentiveSelectors.getAppliedConditionalIncentivesForDisplay(state, offerType).map((item) => {
            return {
                name: item.name,
                value: item.amount
            };
        })
    };
};

export const getRebatesBreakdown = (state: GenericObject, offerType: OfferType): DetailedBreakdown => {
    return {
        value: incentiveSelectors.getAppliedIncentivesTotalForNonDealerCash(state),
        items: incentiveSelectors.getAppliedIncentivesWithFilter(state, offerType, isNotDealerCash).map((item) => {
            return {
                name: item.name,
                value: item.amount
            };
        })
    };
};

export const getNonConditionalIncentivesBreakdown = (state: GenericObject, offerType: OfferType): DetailedBreakdown => {
    return {
        value: incentiveSelectors.getAppliedNonconditionalIncentivesAmount(state),
        items: incentiveSelectors.getNonConditionalsByOfferType(state, offerType).map((item) => {
            return {
                name: item.name,
                value: item.amount
            };
        })
    };
};

// Taxes & Fees
const getTaxRateLabel = (label: string, taxRate: number | TaxItemCategoryItem | undefined) => {
    return `${label}${taxRate ? ` (${taxRate}%)` : ''}`;
};

export const getAdvancedTaxList = (state: GenericObject, offerType: OfferType): any => {
    const taxItems = taxSelectors.getTaxItemsWithAdjustedRates(state, offerType);
    const advanceTaxItems = taxItems.filter((item) => item.taxName === 'advanced');
    const primaryBreakdown = advanceTaxItems[0]?.taxBreakdown;
    if (primaryBreakdown) {
        return [
            {
                name: getTaxRateLabel(TaxLabels.cityTax, primaryBreakdown.cityTaxRate),
                value: primaryBreakdown.cityTax ?? 0
            },
            {
                name: getTaxRateLabel(TaxLabels.countyTax, primaryBreakdown.countyTaxRate),
                value: primaryBreakdown.countyTax ?? 0
            },
            {
                name: getTaxRateLabel(TaxLabels.localTax, primaryBreakdown.localTaxRate),
                value: primaryBreakdown.localTax ?? 0
            },
            {
                name: getTaxRateLabel(TaxLabels.stateTax, primaryBreakdown.stateTaxRate),
                value: primaryBreakdown.stateTax ?? 0
            }
        ];
    }
    return [];
};

export const getAdditionalTaxList = (state: GenericObject) => {
    const additionalTaxBreakdown = taxSelectors.getAdditionalTaxBreakdown(state) || {};
    return Object.entries(additionalTaxBreakdown).flatMap(([key, value]) => {
        return {
            name: TaxLabelMappings[key as keyof typeof TaxLabelMappings],
            value
        };
    });
};

export const getCapitalizedTaxTotalUtil = (state: GenericObject) => {
    const capitalizedTaxTotal = selectedTermSelectors.getCapitalizedTaxTotal(state);
    return {
        name: 'Taxes - Capitalized',
        value: capitalizedTaxTotal
    };
};

const getCapitalizedGovernmentFees = (state: GenericObject) => {
    const offerType = offerNodeSelectors.getCurrentOfferType(state);
    const fees = feesSelectors.getGovernmentFees(state)?.fee?.taxFee ?? [];
    return fees.filter((fee) => offerType !== 'lease' || fee.capped);
};

const getUpfrontGovernmentFees = (state: GenericObject) => {
    const offerType = offerNodeSelectors.getCurrentOfferType(state);
    const fees = feesSelectors.getGovernmentFees(state)?.fee?.taxFee ?? [];
    return fees.filter((fee) => offerType === 'lease' && !fee.capped);
};

export const getGovernmentFeesBreakdown = (state: GenericObject): DetailedBreakdown => {
    const fees = getCapitalizedGovernmentFees(state);
    const governmentItems = fees
        .filter((fee) => fee.amount > 0)
        .map((fee) => ({
            name: fee.name,
            value: fee.amount
        }));

    const totalGovernmentFees = governmentItems.reduce((total, { value }) => total + value, 0);

    return {
        value: roundToTwoDecimal(totalGovernmentFees),
        items: governmentItems
    };
};

export const getUpfrontGovernmentFeesItems = (state: GenericObject): DetailedBreakdownItem[] => {
    const fees = getUpfrontGovernmentFees(state);
    return fees
        .filter((fee) => fee.amount > 0)
        .map((fee) => ({
            name: fee.name,
            value: fee.amount
        }));
};

/**
 * DE773776: the Deal Breakdown itemizes only the lender fees that count toward the total, so the
 * Print Proposal has to drop the same rows or the two documents list different fees for one deal.
 * The condition is deliberately the same one the breakdown uses (the local isAnyLenderFeeToggleEnabled
 * in dash-store's compositeSelectors): with neither toggle on, the breakdown lists every lender fee, and
 * filtering here unconditionally would push the two lists apart the other way.
 */
const isLenderFeeFilteringEnabled = (state: GenericObject): boolean =>
    Boolean(
        state.featureToggles?.DE769095_Include_Lender_Fees_In_Add_Ons ||
            state.featureToggles?.US2116905_Enable_Lender_Fees_From_Priced_Term
    );

const isExcludedDealerFee = (fee: IDealerFee): boolean =>
    fee.originalCategory?.toLowerCase() === 'dealer' && !feesSelectors.countsInCalculation(fee);

const selectCappedFees = (state: GenericObject, offerType: OfferType): IDealerFee[] => {
    const offerTerm = offerNodeSelectors.getOfferTerm(state, offerType);
    const dealerFees: IDealerFee[] = offerTerm.dealerFees || [];

    // Filter fees based on offerType
    if (offerType === 'cash') {
        return dealerFees;
    }
    const cappedFees = dealerFees.filter((fee) => fee.dealerFeeCapped);
    return offerType === LEASE ? cappedFees.filter((fee) => !isExcludedDealerFee(fee)) : cappedFees;
};

// The list is mixed; DE773776 is about lender fees, so dealer and government rows are left alone.
const isUncountedLenderFee = (fee: IDealerFee): boolean =>
    fee.originalCategory?.toLowerCase() === 'lender' && !feesSelectors.countsInCalculation(fee);

/**
 * What the row filter removes. Needed because getTaxesAndFeesTotal ends in `term.totalFee`, and Payment
 * Services counts a fee there regardless of its inclusion flag (verified in QA by replaying one payment
 * request with only `includeInCalcIndicator` changed).
 */
const getUncountedLenderFeesTotal = (state: GenericObject, offerType: OfferType): number => {
    if (!isLenderFeeFilteringEnabled(state)) {
        return 0;
    }

    return selectCappedFees(state, offerType)
        .filter(isUncountedLenderFee)
        .reduce((accumulator, cur) => accumulator + cur.dealerFeeAmount, 0);
};

export const getDealerCappedFeesList = (state: GenericObject, offerType: OfferType) => {
    const cappedFees = selectCappedFees(state, offerType);
    const filteredFees = isLenderFeeFilteringEnabled(state) ? cappedFees.filter((fee) => !isUncountedLenderFee(fee)) : cappedFees;

    // Map to the desired structure
    return filteredFees.map((fee) => ({
        name: fee.dealerFeeName,
        value: fee.dealerFeeAmount
    }));
};

export const getCappedFeesList = (state: GenericObject, offerType: OfferType) => {
    const cappedFeesList: DetailedBreakdownItem[] = [];
    const dealerCappedFeesList = getDealerCappedFeesList(state, offerType);
    const governmentFees = getGovernmentFeesBreakdown(state).items;
    if (dealerCappedFeesList.length > 0) {
        cappedFeesList.push(...dealerCappedFeesList);
    }
    if (governmentFees.length > 0) {
        cappedFeesList.push(...governmentFees);
    }
    return cappedFeesList;
};

export const getTotalTaxesAndFeesForLease = (taxesAndFees: BreakdownItem[]) =>
    taxesAndFees.reduce((item, { value }) => item + value, 0);

export const getFeesTotal = (state: GenericObject, offerType: OfferType): number => {
    const cappedFees = getCappedFeesList(state, offerType);
    const totalFees = cappedFees.reduce((total, { value }) => total + value, 0);
    return roundToTwoDecimal(totalFees);
};

const getPrintedDealerFees = (state: GenericObject, offerType: OfferType) => {
    const cappedFees = selectCappedFees(state, offerType);
    const dealerFeesFiltered = cappedFees.filter((fee) => fee.originalCategory?.toLowerCase() === 'dealer');

    return dealerFeesFiltered.map((fee) => ({
        name: fee.dealerFeeName,
        value: fee.dealerFeeAmount
    }));
};

export const getDealerFeesBreakdown = (state: GenericObject, offerType: OfferType): DetailedBreakdown => {
    const items = getPrintedDealerFees(state, offerType);
    const totalDealerFees = items.reduce((total, { value }) => total + value, 0);

    return {
        value: roundToTwoDecimal(totalDealerFees),
        items
    };
};

/**
 * Gets the breakdown of taxes for a given offer type.
 * When tax override is enabled, returns only the total with empty items array.
 *
 * @param state - The application state
 * @param offerType - The type of offer (lease, finance, cash)
 * @returns DetailedBreakdown with tax items and total value
 */
export const getTaxesBreakdown = (state: GenericObject, offerType: OfferType): DetailedBreakdown => {
    const advancedTaxes = getAdvancedTaxList(state, offerType);
    const additionalTaxes = getAdditionalTaxList(state);
    const capitalizedTaxTotal = getCapitalizedTaxTotalUtil(state);
    const taxes: BreakdownItem[] = [];

    if (offerType !== LEASE && advancedTaxes.length > 0) {
        taxes.push(...advancedTaxes);
    }
    if (additionalTaxes.length > 0) {
        taxes.push(...additionalTaxes);
    }
    if (offerType === LEASE) {
        taxes.push(capitalizedTaxTotal);
    }

    return {
        value: roundToTwoDecimal(taxSelectors.getTotalTax(state, offerType)),
        items: taxes
    };
};

export const createPrepareTaxesAndFeesBreakdown =
    (deps: Partial<PrepareTaxesAndFeesBreakdownDeps> = {}) =>
    (state: GenericObject, offerType: OfferType, isZeroDollarDisclosureShown: boolean): DetailedBreakdown => {
        const { getFeesFn = getCappedFeesList, getTaxesFn = getTaxesBreakdown } = deps;
        const taxesBreakdown = getTaxesFn(state, offerType);
        const cappedFees = getFeesFn(state, offerType);
        const taxesAndFees: BreakdownItem[] = [...taxesBreakdown.items];

        if (cappedFees.length > 0) {
            taxesAndFees.push(...cappedFees);
        }

        const filteredTaxesAndFees = isZeroDollarDisclosureShown ? taxesAndFees : taxesAndFees.filter((item) => item.value !== 0);
        // LEASE sums these rows, so it follows the filter already; the others take the Payment Services
        // figure, which still counts the dropped rows, so the same amount comes off here.
        const totalTaxesAndFees =
            offerType === LEASE
                ? getTotalTaxesAndFeesForLease(filteredTaxesAndFees)
                : taxSelectors.getTaxesAndFeesTotal(state, offerType) - getUncountedLenderFeesTotal(state, offerType);

        return { value: roundToTwoDecimal(totalTaxesAndFees), items: filteredTaxesAndFees };
    };

const prepareTaxesAndFeesBreakdown = createPrepareTaxesAndFeesBreakdown();

export const getTaxesAndFeesBreakdown = (
    state: GenericObject,
    offerType: OfferType,
    isZeroDollarDisclosureShown = false
): DetailedBreakdown => {
    const shopperZipCode = shopperSelectors.getShopperZip(state);
    return {
        ...prepareTaxesAndFeesBreakdown(state, offerType, isZeroDollarDisclosureShown),
        subHeader: `Zip Code ${shopperZipCode}`
    };
};

/**
 * Gets the customizable breakdown of taxes and fees for a given offer type.
 * When tax override is enabled, prepends 'Total Taxes' to the items list.
 * Supports zero-value disclosure based on the flag.
 *
 * @param state - The application state
 * @param offerType - The type of offer (lease, finance, cash)
 * @param isZeroDollarDisclosureShown - Whether to include zero-value items in the breakdown
 * @returns DetailedBreakdown with tax and fee items and total value
 */
export const getCustomizableTaxesAndFeesBreakdown = (
    state: GenericObject,
    offerType: OfferType,
    isZeroDollarDisclosureShown = false
): DetailedBreakdown => {
    const prepareTaxesAndFeesBreakdownFn = createPrepareTaxesAndFeesBreakdown({
        getFeesFn: (state, offerType) =>
            getCappedFeesList(state, offerType).map((fee, index) => ({
                ...fee,
                name: fee.name || `Fee ${index + 1}`
            }))
    });

    const taxesAndFeesBreakdown = prepareTaxesAndFeesBreakdownFn(state, offerType, isZeroDollarDisclosureShown);
    const flatTax = taxSelectors.getOfferFlatTaxItem(state, offerType);

    if (flatTax?.isManualTax) {
        return {
            ...taxesAndFeesBreakdown,
            items: [{ name: TaxLabels.totalTaxes, value: flatTax.taxAmount }, ...taxesAndFeesBreakdown.items]
        };
    }

    return taxesAndFeesBreakdown;
};

// Vehicle Protection
const mapProtectionListProduct = (item: any): DetailedBreakdownItem => {
    const productName = item.cardHeading || item.productName;
    const productPrice = item.totalPrice || item.productPrice;

    return { name: productName, value: productPrice };
};

const defaultProtectionBreakdownItem = (item: DetailedBreakdownItem): DetailedBreakdownItem => {
    return { name: item.name ?? '', value: item.value ?? 0 };
};

const createMapProductWithDefaultName = () => {
    const productsCountByCategory = new Map<string, number>();

    return (product: any) => {
        const breakdownItem = mapProtectionListProduct(product);

        if (!breakdownItem.name) {
            const productCategory = product.productCategoryCode;

            if (productCategory) {
                const upperCaseProductCategory = productCategory.toUpperCase();
                const productsCount = productsCountByCategory.get(upperCaseProductCategory) || 0;

                breakdownItem.name = `${upperCaseProductCategory} ${productsCount + 1}`;

                productsCountByCategory.set(upperCaseProductCategory, productsCount + 1);
            }
        }

        return breakdownItem;
    };
};

const createGetVehicleProtecionList =
    ({ convertToBreakdownItem }: { convertToBreakdownItem: (item: any) => DetailedBreakdownItem }) =>
    (state: GenericObject) => {
        const currentOfferType = offerNodeSelectors.getCurrentOfferType(state);

        return vehicleProtectionSelectors
            .getVppSelectedProducts(state)
            .filter((product: any) => product.offerTypes?.includes(currentOfferType) ?? true)
            .map((product: any) => defaultProtectionBreakdownItem(convertToBreakdownItem(product)));
    };

export const getVehicleProtectionList = createGetVehicleProtecionList({
    convertToBreakdownItem: mapProtectionListProduct
});

export const getVehicleProtectionBreakdown = (state: GenericObject, isZeroDollarDisclosureShown = false): DetailedBreakdown => {
    const vppItems = createGetVehicleProtecionList({ convertToBreakdownItem: createMapProductWithDefaultName() })(state);

    return {
        value: vehicleProtectionSelectors.getVppSummary(state).totalProductPrice,
        items: isZeroDollarDisclosureShown ? vppItems : vppItems.filter((item: any) => item.value !== 0)
    };
};

export const getTradeSourceDisplayValue = (tradeSource: string) => {
    switch (tradeSource) {
        case 'ICO':
            return 'KBB Instant Cash Offer';
        case 'SUBARUGTP':
            return 'Subaru Guaranteed Trade-in Value';
        case 'SD':
        default:
            return 'Trade-In Value';
    }
};

// Trade-In Value Applied
export const getTradeInSource = (state: GenericObject) => {
    const tradeSource = tradeInSelectors.getTradeSource(state);
    return getTradeSourceDisplayValue(tradeSource);
};

export const getTradeInList = (state: GenericObject) => {
    const tradeSource = getTradeInSource(state);
    const tradeValue = tradeInSelectors.getTradeValue(state);
    const tradeAmountOwed = tradeInSelectors.getTradeInAmountOwedFromOffer(state);
    const tradeInItems: DetailedBreakdown['items'] = [];
    tradeInItems.push({
        name: tradeSource,
        value: tradeValue
    });
    if (tradeAmountOwed > 0) {
        tradeInItems.push({
            name: 'Amount Owed On Trade-In',
            value: tradeAmountOwed
        });
    }
    return tradeInItems;
};

export const getMultiTradeInEquityBreakdown = (state: GenericObject): NetTradeInEquity => {
    const trades = multipleTradeInsSelectors.getIncludedTradeInsWithOriginalIndexes(state);
    return {
        value: multipleTradeInsSelectors.getTotalNetTradeValueForIncludedActiveTradeIns(state),
        items: trades.reduce((finalItems: DetailedBreakdownItem[], trade: any) => {
            const tradeYear = multipleTradeInsSelectors.getTradeYear(state, trade.originalIndex);
            const tradeMake = multipleTradeInsSelectors.getTradeMake(state, trade.originalIndex);
            const tradeModel = multipleTradeInsSelectors.getTradeModel(state, trade.originalIndex);
            const tradeTrim = multipleTradeInsSelectors.getTradeTrim(state, trade.originalIndex);
            const tradeValueTitle = getTradeSourceDisplayValue(
                multipleTradeInsSelectors.getTradeSource(state, trade.originalIndex)
            );
            const tradeValue = multipleTradeInsSelectors.getTradeValue(state, trade.originalIndex);
            const tradeAmountOwed = multipleTradeInsSelectors.getTradeInAmountOwed(state, trade.originalIndex);
            const name = `${tradeYear} ${tradeMake} ${tradeModel} ${tradeTrim}`;
            return [
                ...finalItems,
                {
                    name,
                    value: tradeValue - tradeAmountOwed,
                    children: [
                        { name: tradeValueTitle, value: tradeValue },
                        { name: 'Amount Owed On Trade-In', value: tradeAmountOwed }
                    ]
                }
            ];
        }, [] as DetailedBreakdownItem[]),
        tradeAllowance: multipleTradeInsSelectors.getIncludedTradeInsTotalValue(state),
        subHeader: trades
            .map(({ tradeIn }: { tradeIn: any }) =>
                [tradeIn.year, tradeIn.make, tradeIn.model, tradeIn.trim].filter(Boolean).join(' ')
            )
            .join(' | ')
    };
};

export const getNetTradeInEquityBreakdown = (state: GenericObject): NetTradeInEquity => {
    const tradeMake = tradeInSelectors.getTradeMake(state);
    const tradeModel = tradeInSelectors.getTradeModel(state);
    const tradeTrim = tradeInSelectors.getTradeTrim(state);
    const tradeYear = tradeInSelectors.getTradeYear(state);
    const tradeNetValue = tradeInSelectors.getTradeNetValue(state);
    const tradeValue = tradeInSelectors.getTradeValue(state);
    const tradeInItems = tradeValue ? getTradeInList(state) : [];
    const tradeSubHeader = tradeValue ? `${tradeYear} ${tradeMake} ${tradeModel} ${tradeTrim}` : '';
    return {
        value: tradeNetValue,
        subHeader: tradeSubHeader,
        items: tradeInItems
    };
};

// Est. Due At Signing (Lease Only)
export const getDealerUpfrontFeesList = (state: GenericObject) => {
    const dealerFees = feesSelectors.getDealerFees(state);
    const filterDealerFees = dealerFees.filter((fee: IDealerFee) => fee.dealerFeeCapped === false);
    return filterDealerFees.map((fee) => ({
        name: fee.dealerFeeName,
        value: fee.dealerFeeAmount
    }));
};

export const getLenderUpfrontFeesList = (state: GenericObject) => {
    const selectedTerm = offerNodeSelectors.getSelectedTerm(state);
    const lenderFees = feesSelectors.getDealerLenderFeesFromTerm(selectedTerm, true);
    const filterLenderFees = lenderFees.filter(
        (fee: IDealerFee) => fee.originalCategory.toLowerCase() === 'lender' && fee.dealerFeeCapped === false
    );
    return filterLenderFees.map((fee) => ({
        name: fee.dealerFeeName,
        value: fee.dealerFeeAmount
    }));
};

export const getUpfrontTaxTotalUtil = (state: GenericObject) => {
    const upfrontTaxTotal = selectedTermSelectors.getUpfrontTaxTotal(state);
    return {
        name: 'Taxes - Due Upfront',
        value: upfrontTaxTotal
    };
};

export const getNetDownPayment = (state: GenericObject) => {
    const netCashDown = selectedTermSelectors.getNetCashDown(state);
    return {
        name: 'Net Down Payment',
        value: netCashDown
    };
};

export const getFirstMonthPaymentUtil = (state: GenericObject) => {
    const monthlyPayment = selectedTermSelectors.getFirstMonthPayment(state);
    return {
        name: 'First Month Payment',
        value: monthlyPayment
    };
};

export const getDeferredAmountAdjustment = (state: GenericObject) => {
    const applicable =
        !!offerNodeSelectors.getIsTargetOutOfPocket &&
        selectedTermSelectors.isTargetOutOfPocketLeaseEnabled(state) &&
        selectedTermSelectors.isDealSummaryLayoutV2Enabled(state);
    const cashOffsetAmountAddedToPrice = selectedTermSelectors.getCashOffsetAmountAddedToPrice(state);

    return {
        name: 'Deferred Amount Adjustment',
        value: applicable ? cashOffsetAmountAddedToPrice * -1 : 0
    };
};

export const getEstDueAtSigningFeesList = (
    state: GenericObject,
    isFirstPaymentCapitalized = selectedTermSelectors.getFirstPaymentCapitalized(state)
) => {
    const feesList: { name: string; value: number }[] = [];
    const upfrontTaxTotal = getUpfrontTaxTotalUtil(state);
    const netCashDown = getNetDownPayment(state);
    const upfrontGovernmentFees = getUpfrontGovernmentFeesItems(state);
    const dealerUpfrontFees = getDealerUpfrontFeesList(state);
    const lenderUpfrontFees = getLenderUpfrontFeesList(state);
    const monthlyPayment = getFirstMonthPaymentUtil(state);
    const deferredAmountAdjustment = getDeferredAmountAdjustment(state);
    feesList.push(upfrontTaxTotal);
    feesList.push(netCashDown);
    if (upfrontGovernmentFees.length > 0) {
        feesList.push(...upfrontGovernmentFees);
    }
    if (dealerUpfrontFees.length > 0) {
        feesList.push(...dealerUpfrontFees);
    }
    feesList.push(...lenderUpfrontFees);
    if (!isFirstPaymentCapitalized) {
        feesList.push(monthlyPayment);
    }
    feesList.push(deferredAmountAdjustment);
    return feesList;
};

export const getEstDueAtSigningBreakdown = (state: GenericObject, offerType: OfferType, isFirstPaymentCapitalized?: boolean) => {
    const useDiaDueAtSigning = selectedTermSelectors.isDe771200DueAtSigningFixEnabled(state) && offerType === LEASE;
    const dueAtSigning = useDiaDueAtSigning
        ? selectedTermSelectors.getDiaDueAtSigning(state)
        : feesSelectors.getDueAtSigning(state, offerType);
    const estDueAtSigningFees = getEstDueAtSigningFeesList(state, isFirstPaymentCapitalized);
    const filteredEstDueAtSigningFees = estDueAtSigningFees.filter((item) => item.value !== 0);
    return {
        value: dueAtSigning,
        items: filteredEstDueAtSigningFees.map((item) => {
            return {
                name: item.name,
                value: item.value
            };
        })
    };
};
